























































import { Component, Prop, Vue } from "vue-property-decorator";
import { Department } from "@/typings";

interface OrbArt {
  statue: string;
  globe: string;
  // Size of the statue image, and where its globe is (centre and radius), in image pixels
  w: number;
  h: number;
  cx: number;
  cy: number;
  r: number;
}

interface Orb {
  kind: string;
  value: number;
  label: string;
  detail: string;
  critical: boolean;
  art: OrbArt;
  // How much of the globe image to clip from the top, in percent of its height
  cut: number;
  globeBox: { [prop: string]: string };
}

// The life and mana statues from Diablo II's control panel: the stone woman holding the life globe
// and the demon holding the mana globe. Each statue image has a hole where its globe shows, and the
// globe image under it empties from the top down, the way the game draws it.
const ART: { [kind: string]: OrbArt } = {
  life: {
    statue: require("@/assets/images/diablo/statue-life.png"),
    globe: require("@/assets/images/diablo/globe-life.png"),
    w: 212,
    h: 170,
    cx: 124.7,
    cy: 74.8,
    r: 72.4,
  },
  mana: {
    statue: require("@/assets/images/diablo/statue-mana.png"),
    globe: require("@/assets/images/diablo/globe-mana.png"),
    w: 211,
    h: 196,
    cx: 87.9,
    cy: 88.5,
    r: 72.4,
  },
};

const percent = (n: number): string => `${n.toFixed(2)}%`;

// The Diablo II HUD. The orbs follow the current course set: mana is how many schedules QuACS can
// still build for you, life drains as the workload climbs past 14 credits and drops to critical when
// every schedule has a conflict.
@Component
export default class DiabloDecor extends Vue {
  @Prop({ required: true }) readonly place!: string;

  images = {
    portal: require("@/assets/images/diablo/town-portal.gif"),
    waypoint: require("@/assets/images/diablo/waypoint.gif"),
    belt: [
      require("@/assets/images/diablo/potion-healing.gif"),
      require("@/assets/images/diablo/potion-superhealing.gif"),
      require("@/assets/images/diablo/potion-mana.gif"),
      require("@/assets/images/diablo/potion-rejuv.gif"),
    ],
  };

  get selectedCrns(): string[] {
    const schedule = this.$store.state.schedule;
    const set = schedule.courseSets[schedule.currentTerm]?.[
      schedule.currentCourseSet
    ] as { [crn: string]: boolean } | undefined;
    return set ? Object.keys(set).filter((crn) => set[crn]) : [];
  }

  // Credits planned: the smallest credit value of each course with a selected section
  get credits(): number {
    const selected = new Set(this.selectedCrns.map(Number));
    if (selected.size === 0) {
      return 0;
    }
    let total = 0;
    for (const dept of this.$store.state.departments as Department[]) {
      for (const course of dept.courses) {
        if (course.sections.some((section) => selected.has(section.crn))) {
          total += Math.min(...course.sections.map((s) => s.credMin));
        }
      }
    }
    return total;
  }

  get schedules(): number {
    return this.$store.getters["schedule/numSchedules"] ?? 0;
  }

  get orbs(): Orb[] {
    const planning = this.selectedCrns.length > 0;
    const conflicted = planning && this.schedules === 0;
    let life = 100 - Math.max(0, this.credits - 14) * 10;
    if (conflicted) {
      life = Math.min(life, 12);
    }
    life = Math.max(6, Math.min(100, life));
    const mana = !planning
      ? 100
      : Math.round(
          Math.max(4, Math.min(100, 30 * Math.log10(this.schedules + 1)))
        );
    return [
      this.orb(
        "life",
        life,
        `Life: ${life} / 100`,
        conflicted
          ? "Every schedule has a conflict"
          : `${this.credits} credits planned`,
        life < 25
      ),
      this.orb(
        "mana",
        mana,
        `Mana: ${mana} / 100`,
        planning
          ? `${this.schedules} possible schedule${
              this.schedules === 1 ? "" : "s"
            }`
          : "Pick some courses",
        false
      ),
    ];
  }

  orb(
    kind: string,
    value: number,
    label: string,
    detail: string,
    critical: boolean
  ): Orb {
    const art = ART[kind];
    const surface = art.cy + art.r - 2 * art.r * (value / 100);
    return {
      kind,
      value,
      label,
      detail,
      critical,
      art,
      cut: (100 * surface) / art.h,
      globeBox: {
        left: percent((100 * (art.cx - art.r)) / art.w),
        top: percent((100 * (art.cy - art.r)) / art.h),
        width: percent((200 * art.r) / art.w),
        height: percent((200 * art.r) / art.h),
      },
    };
  }

  toTop(): void {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }
}
